import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

// Kompakter IFB-Regler (§10 EStG, nur AT) fürs Forecast-Dashboard.
// August 2026: Maximum basiert auf dem PROGNOSTIZIERTEN Jahresgewinn (Ist + Forecast,
// vom Forecast-Endpoint geliefert) — und ist bewusst UNABHÄNGIG vom Regler im
// Steuern-Tab (eigener Settings-Key). Vollversion mit Schwellen-Breakdown: Taxes.js.
export const InvestmentAllowanceSlider = ({ maxAmount, selectedAmount, savingsCurve, onChanged }) => {
  const { user } = useAuth();
  const [amount, setAmount] = useState(selectedAmount || 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAmount(selectedAmount || 0);
  }, [selectedAmount]);

  // Lineare Interpolation auf der Backend-Ersparnis-Kurve (ESt ist stückweise linear)
  const savingsFor = (val) => {
    const curve = savingsCurve;
    if (!curve?.length) return null;
    if (val <= curve[0][0]) return curve[0][1];
    for (let i = 1; i < curve.length; i++) {
      const [a1, s1] = curve[i - 1];
      const [a2, s2] = curve[i];
      if (val <= a2) return a2 === a1 ? s2 : s1 + ((val - a1) / (a2 - a1)) * (s2 - s1);
    }
    return curve[curve.length - 1][1];
  };
  const liveSavings = savingsFor(Math.min(amount, maxAmount || 0));

  if (user?.country !== "AT" || maxAmount == null) return null;

  if (maxAmount <= 0) {
    return (
      <p className="text-[10px] text-slate-400" data-testid="forecast-ifb-hint">
        Investitionsfreibetrag (§10 EStG): greift erst ab 33.000 € prognostiziertem Jahresgewinn nach SVS — Details im Steuern-Tab.
      </p>
    );
  }

  const commit = async (val) => {
    setSaving(true);
    try {
      await api.put("/settings", { at_forecast_investment_allowance_amount: val });
      onChanged?.();
    } catch {
      toast.error("Fehler beim Speichern des Investitionsfreibetrags");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-3 border-t border-dashed border-slate-200 space-y-2" data-testid="forecast-ifb-slider">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          Investitionsfreibetrag (§10 EStG)
        </p>
        <div className="text-right">
          <p className="text-sm font-medium font-mono text-emerald-600" data-testid="forecast-ifb-amount">{fmt(amount)}</p>
          {liveSavings != null && (
            <p className="text-[11px] font-medium text-emerald-600" data-testid="forecast-ifb-savings">
              spart {fmt(liveSavings)} ESt
            </p>
          )}
        </div>
      </div>
      <Slider
        value={[Math.min(amount, maxAmount)]}
        onValueChange={(v) => setAmount(v[0])}
        onValueCommit={(v) => commit(v[0])}
        max={maxAmount}
        min={0}
        step={100}
        disabled={saving}
        className="w-full"
        data-testid="forecast-ifb-slider-input"
      />
      <div className="flex items-center justify-between text-[10px] text-slate-400">
        <span>0 €</span>
        <span>max. {fmt(maxAmount)}</span>
      </div>
      <p className="text-[10px] text-slate-400">
        Geplante Investition in begünstigtes Anlagevermögen (Behaltefrist 4 Jahre) — senkt die prognostizierte Steuerlast sofort.
        Maximum basiert auf dem prognostizierten Jahresgewinn (Ist + Forecast). Unabhängig vom Regler im Steuern-Tab.
      </p>
    </div>
  );
};

export default InvestmentAllowanceSlider;
